import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {FORM_ERROR} from 'final-form';
import {Box} from '@tenancy.nz/design-system';
import {GACreateBooking, GACreateEnquiry} from '../../analytics';
import BookingForm from '../../components/BookingForm';
import BookingFormHeader from '../../components/BookingFormHeader';
import BookingSuccess from '../../components/BookingSuccess';
import EnquirySuccess from '../../components/EnquirySuccess';
import {useInitAgencyBySlugQuery} from '../../api/main/agency';
import {useGetProfileQuery} from '../../api/main/tenant';
import {useCreateBookingMutation} from '../../api/bookme/booking';
import {useCreateEnquiryMutation} from '../../api/bookme/enquiry';
import {useGetUpcomingViewingsByPropertyQuery} from '../../api/bookme/viewing';
import {
  env,
  handleBookingSubmitErrors,
  handleEnquirySubmitErrors,
  prepareBookingPayload,
  prepareEnquiryPayload,
  prepareMobileNumber,
  toNumber,
  prepareFormInitialValues,
  redirect,
  setBookingDetailsCookie,
  isArray,
} from '../../utils/helpers';
import scrollTo from '../../utils/helpers/scrollTo';
import * as consts from '../../utils/constants';

function BookingFeature() {
  const {id} = useParams();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const {auth, booking} = useSelector(state => state);
  const [searchParams, setSearchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);

  const [createBooking] = useCreateBookingMutation();
  const [createEnquiry] = useCreateEnquiryMutation();

  const {data: tenant = {}, isLoading: isLoadingProfile} = useGetProfileQuery(
    undefined,
    {
      skip: !auth.authenticated,
    }
  );

  const {
    data: {prepared} = {prepared: {}},
    isError,
    isLoading,
  } = useInitAgencyBySlugQuery(id, {
    skip: !id,
  });
  const {agency, property} = prepared;

  const {
    data: {prepared: viewings} = {prepared: {}},
    isLoading: isLoadingViewings,
  } = useGetUpcomingViewingsByPropertyQuery(
    {
      id,
      options: {
        query: {
          [consts.API_PARAM_ATTENDEE_COUNT]: 1,
          [consts.API_PARAM_BOOKING_COUNT]: 1,
          [consts.API_PARAM_SORT]: 'date_time',
        },
      },
    },
    {skip: !id || isLoading || isError}
  );

  const enquiryToken = searchParams.get(consts.URL_PARAM_ENQUIRY_TOKEN);
  const submitType = searchParams.get(consts.URL_PARAM_VIEWING);
  useEffect(() => {
    if (!submitted && !!submitType) {
      searchParams.delete(consts.URL_PARAM_VIEWING);
      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submittedViewing = useMemo(() => {
    if (!isArray(viewings)) {
      return {date: '', time: ''};
    }
    const [date, time] = (
      viewings.find(v => v.id === toNumber(submitType)) || {localDateTime: ''}
    ).localDateTime.split(' ');
    return {
      date,
      time,
    };
  }, [submitType, viewings]);

  const analyticsData = {
    tps_referrer_code: searchParams.get('referrer'),
    tps_referrer_url: document.referrer ?? null,
  };

  const handleBookingSubmit = values => {
    try {
      // Do pre post validation
      const totalAttendees = toNumber(
        values[consts.FORM_FIELD_TOTAL_ATTENDEES]
      );
      const viewingId = toNumber(values[consts.FORM_FIELD_VIEWING_DAYS]);
      const selectedViewing = viewings.find(time => time.id === viewingId);
      const {
        totalBookings,
        totalBookingsAllowed,
        totalAllowed,
        maxViewersType,
        actualBookingCount,
      } = selectedViewing;
      const maxAttendeesAllowed = totalAllowed - totalBookings;

      // If the total bookings + 1 is greater than the allowed bookings
      if (
        maxViewersType !== 'MAX_ATTENDEES' &&
        actualBookingCount >= totalBookingsAllowed
      ) {
        return {
          [FORM_ERROR]: `Sorry, all ${totalBookingsAllowed} bookings have been filled on this property.`,
        };
      }

      if (
        maxViewersType !== 'MAX_BOOKINGS' &&
        totalAttendees > maxAttendeesAllowed
      ) {
        return {
          [consts.FORM_FIELD_TOTAL_ATTENDEES]: `Sorry, only ${maxAttendeesAllowed} spaces available.`,
        };
      }

      const payload = {
        ...prepareBookingPayload(values),
        ...(!!enquiryToken && {
          [consts.BOOKING_ENQUIRY_TOKEN]: enquiryToken,
        }),
      };

      return createBooking(payload)
        .unwrap()
        .then(response => {
          if (response.data.id) {
            setSubmitted(true);
            const tenantPortalUrl = env('REACT_APP_TENANT_PORTAL_URL');
            if (response.data.account === false && tenantPortalUrl) {
              setBookingDetailsCookie(response.data);
              redirect(`${tenantPortalUrl}/register`);
            } else {
              navigate(`?${consts.URL_PARAM_VIEWING}=${viewingId}`);
              scrollTo(panelRef.current);
            }
            // Log booking creation analytics
            GACreateBooking({
              ...analyticsData,
              viewingId,
              totalAttendees,
            });
          }
        })
        .catch(handleBookingSubmitErrors);
    } catch (err) {
      return {[FORM_ERROR]: 'Something went wrong'};
    }
  };

  const handleEnquirySubmit = values => {
    const payload = prepareEnquiryPayload({
      ...values,
      [consts.ENQUIRY_AGENCY_ID]: agency.id,
      [consts.ENQUIRY_FULL_ADDRESS]: property.address,
      [consts.ENQUIRY_PROPERTY_ID]: property.id,
    });
    return createEnquiry(payload)
      .unwrap()
      .then(response => {
        if (response.data.id) {
          setSubmitted(true);
          navigate(`?${consts.URL_PARAM_VIEWING}=0`);
          scrollTo(panelRef.current);

          // Log enquiry creation analytics
          GACreateEnquiry({
            ...analyticsData,
            propertyId: payload[consts.ENQUIRY_PROPERTY_ID],
            agencyId: payload[consts.ENQUIRY_AGENCY_ID],
          });
        }
      })
      .catch(handleEnquirySubmitErrors);
  };

  const handleSubmit = values => {
    const sanitizedValues = {
      ...values,
      [consts.FORM_FIELD_MOBILE]: prepareMobileNumber(
        values[consts.FORM_FIELD_MOBILE]
      ),
    };
    if (
      sanitizedValues[consts.FORM_FIELD_IS_ENQUIRY] === true ||
      sanitizedValues[consts.FORM_FIELD_IS_ENQUIRY] === '1'
    ) {
      return handleEnquirySubmit(sanitizedValues);
    }
    return handleBookingSubmit(sanitizedValues);
  };

  const handleBackToListings = () => {
    navigate(`/tps${agency.clientCode}`);
  };

  return (
    <React.Fragment>
      {submitted && submitType !== '0' && (
        <BookingSuccess
          address={property.address}
          viewingsUrl={`${env('REACT_APP_TENANT_PORTAL_URL')}/viewings`}
          viewingDate={submittedViewing.date}
          viewingTime={submittedViewing.time}
        />
      )}
      {submitted && submitType === '0' && (
        <EnquirySuccess
          address={property.address}
          onBack={handleBackToListings}
        />
      )}
      {!isLoading && (!submitted || !submitType) && (
        <Box ref={panelRef}>
          <Box mb="20px">
            <BookingFormHeader heading="Book a Viewing:" loading={isLoading} />
          </Box>
          <BookingForm
            // captcha={!auth.authenticated}
            captcha
            heading={property.address}
            loading={isLoadingViewings || isLoadingProfile}
            onSubmit={handleSubmit}
            supHeading="Viewing for"
            viewings={viewings}
            initialValues={{
              [consts.FORM_FIELD_TERMS]: false,
              ...prepareFormInitialValues(tenant.profile),
              ...booking.formValues,
              ...(!(isLoadingViewings || isLoadingProfile) &&
                viewings.length === 0 && {
                  [consts.FORM_FIELD_IS_ENQUIRY]: '1',
                }),
            }}
          />
        </Box>
      )}
    </React.Fragment>
  );
}

export default BookingFeature;
